
import {
  IMqttMessage,
  IMqttServiceOptions,
  IPublishOptions,
  MqttService,
} from 'ngx-mqtt';
import { Subscription } from 'rxjs';
import { IClientSubscribeOptions, QoS } from 'mqtt-browser';

import { Utils } from '../../utils/utils';
import { TelemetryDataHelper } from 'src/app/models/device/telemetry-details.model';
import { EmqxRecieveMessageModel, MqttMessageType } from './emqx-receive-message.model';
import { NotifierHelper } from './common.service';

export class EmqxSubscription {
  subscription?: Subscription;

  private isConnected: boolean = false;
  private isSetupCompleted: boolean = false;
  private isSetupCalling = false;

  #client?: MqttService;
  #isConnecting: boolean = false;
  #topic?: string;
  #isSubscribed: boolean = false;
  lastConnectionCheckTime = 0;
  private connection?: IMqttServiceOptions;

  constructor(
    private readonly notifier: NotifierHelper<EmqxRecieveMessageModel>
  ) {}

  private get canConnect(): boolean {
    return this.#client !== undefined && !this.#isConnecting;
  }

  private get needToReconnect(): boolean {
    return !this.isSetupCompleted || this.canConnect;
  }

  private canSetup(): boolean {
    return !this.isSetupCalling && !this.#isConnecting;
  }

  setup(connection: IMqttServiceOptions, isCalledWhenOnline: boolean): void {
    if (this.canSetup()) {
      try {
        this.isSetupCalling = false;
        this.isSetupCompleted = false;
        this.connection = { ...connection };
        this.connection.clientId =
          'datastudio-' +
          Date.now() +
          '-' +
          crypto.getRandomValues(new Uint32Array(1));
        this.forceConnect();
        this.isSetupCompleted = true;
      } catch (e) {}
      this.isSetupCalling = false;
    }
  }

  private async forceConnect() {
    if (this.connection) {
      this.#isConnecting = true;
      this.justDisconnectClient();
      await Utils.delay(1000);
      this.#client = new MqttService(this.connection);
      this.#isConnecting = false;
      this.#isSubscribed = false;
      this.onConnect();
    }
  }

  private async clientConnect() {
    if (!this.#isConnecting) {
      if (this.connection && !this.isConnected) {
        await this.forceConnect();
      } else if (this.connection) {
        this.onConnect();
      }
    }
  }

  private onConnect() {
    if (this.canConnect) {
      this.#isConnecting = true;
      this.#client?.onConnect.subscribe(() => {
        this.onConnectionSuccess();
        this.#isConnecting = false;
        this.isConnected = true;
      });
      this.#client?.onError.subscribe((_: any) => {
        this.#isConnecting = false;
        this.isConnected = false;
        this.isSetupCompleted = false;
      });
      this.#client?.connect(this.connection);
    }
  }

  private async onConnectionSuccess() {
    if (this.#topic && !this.#isSubscribed) {
      await this.subscribeTopic(this.#topic);
    }
  }

  private justDisconnectClient() {
    try {
      if (this.#client) {
        this.subscription?.unsubscribe();
        this.#client?.disconnect();
      }
    } catch (_) {}
  }

  disconnect(): void {
    try {
      this.#isConnecting = false;
      this.subscription?.unsubscribe();
      this.#client?.disconnect();
    } catch (_) {}
  }

  private canSubscribeTopic(topic: string) {
    return (
      topic &&
      (!this.#isSubscribed || (this.#topic != topic && !this.subscription))
    );
  }

  async subscribe(
    topic: string,
    addToWaitingList: boolean = true
  ): Promise<boolean> {
    this.#topic = topic;
    if (this.canSubscribeTopic(topic)) {
      if (this.needToReconnect) {
        this.#isSubscribed = false;
        if (addToWaitingList) {
          await this.clientConnect();
        }
      } else {
        await this.subscribeTopic(topic);
      }
    }
    return false;
  }

  private async subscribeTopic(topic: string): Promise<boolean> {
    let value: boolean = false;
    if (this.canSubscribeTopic(topic)) {
      try {
        value = await this.subscribeAndAddToMemory(topic);
      } catch (_) {}
    }
    return value;
  }
  private async subscribeAndAddToMemory(topic: string): Promise<boolean> {
    const qos: QoS = 0;
    const subscription = this.#client
      ?.observeRetained(topic, { qos } as IClientSubscribeOptions)
      .subscribe((message) => {
        this.onMessageReceived(message);
      });
    if (subscription) {
      this.subscription = subscription;
      this.#isSubscribed = true;
    }
    return true;
  }

  unsubscribe(): void {
    try {
      this.subscription?.unsubscribe();
      this.#isSubscribed = false;
      this.#topic = undefined;
    } catch (_) {}
  }

  private isWillMessage(message: string) {
    return message === '"will message"';
  }

  private onMessageReceived(message: IMqttMessage): void {
    if (!message.retain){

    try {
      let payload: string = new TextDecoder().decode(message.payload);
      if (!this.isWillMessage(payload.trim().toLowerCase())) {
        payload = this.checkAndSetViewUpdatePayload(payload);
        const telemetry = new Map(Object.entries(JSON.parse(payload)));
        this.notifier.notify(
          this.setEmqxModel(telemetry as Map<string, any>, message)
        );
      }
    } catch (_) {}}
  }

  private checkAndSetViewUpdatePayload(payload: string): string {
    if (!payload.includes('{') && !payload.includes('[')) {
      payload = `{"itemId":"${payload}"}`;
    }
    return payload;
  }

  private setEmqxModel(
    payload: Map<string, any>,
    message: IMqttMessage
  ): EmqxRecieveMessageModel {
    const model: EmqxRecieveMessageModel = {
      topic: message.topic,
      payload: payload,
      message: message,
      type: MqttMessageType.None,
    };
    this.checkIsProjectUpdate(payload, model) ||
      this.checkIsDeviceStatus(payload, model) ||
      this.checkIsSensorTelemetry(payload, model) ||
      this.checkIsFlowChartTelemetry(payload, model) ||
      this.checkFlowChartSyncStatus(payload, model);

    return model;
  }

  private checkIsProjectUpdate(
    payload: Map<string, any>,
    model: EmqxRecieveMessageModel
  ): boolean {
    let value = payload.has('itemId');
    if (value) {
      this.onItemUpdateRecieved(model, payload);
    }
    return value;
  }

  private checkIsDeviceStatus(
    payload: Map<string, any>,
    model: EmqxRecieveMessageModel
  ): boolean {
    let value = payload.has('DeviceStatus');
    if (value) {
      this.onDeviceStatusRecieved(model, payload);
    }
    return value;
  }

  private checkIsSensorTelemetry(
    payload: Map<string, any>,
    model: EmqxRecieveMessageModel
  ): boolean {
    let value = payload.has('sensor_telemetry');
    if (value) {
      this.onSensorTelemetryReceived(model, payload);
    }
    return value;
  }

  private checkIsFlowChartTelemetry(
    payload: Map<string, any>,
    model: EmqxRecieveMessageModel
  ): boolean {
    let value = payload.has('call_type');
    if (value) {
      this.onFlowChartTelemetryReceived(model);
    }
    return value;
  }
  private checkFlowChartSyncStatus(
    payload: Map<string, any>,
    model: EmqxRecieveMessageModel
  ): boolean {
    let value = payload.has('sync_status');
    if (value) {
      this.onFlowChartStatusTelemetryReceived(model);
    }
    return value;
  }

  private onItemUpdateRecieved(
    model: EmqxRecieveMessageModel,
    payload: Map<string, any>
  ) {
    model.type = MqttMessageType.ItemUpdate;
    model.updatedItemId = payload.get('itemId');
  }

  private onDeviceStatusRecieved(
    model: EmqxRecieveMessageModel,
    payload: Map<string, any>
  ) {
    model.type = MqttMessageType.DeviceStatus;
    model.deviceStatus = payload.get('DeviceStatus');
  }

  private onSensorTelemetryReceived(
    model: EmqxRecieveMessageModel,
    payload: Map<string, any>
  ) {
    model.type = MqttMessageType.SensorValues;
    model.sensorValues = payload.get('sensor_telemetry');
    if (model.sensorValues?.[0]?.fields) {
      model.sensorValues[0].values = TelemetryDataHelper.setValues(
        model.sensorValues[0].fields,
        false
      );
    }
  }

  private onFlowChartTelemetryReceived(model: EmqxRecieveMessageModel) {
    model.type = MqttMessageType.FlowChartDebugStatus;
  }

  private onFlowChartStatusTelemetryReceived(model: EmqxRecieveMessageModel) {
    model.type = MqttMessageType.FlowChartSyncStatus;
  }

  publishMessage(topic: string, payload: string): boolean {
    try {
      const qos: QoS = 0;
      this.#client?.unsafePublish(topic, payload, { qos } as IPublishOptions);
      return true;
    } catch (_) {}
    return false;
  }
}
